// The IRC layout's windows (#154). The switchbar (IrcStrip): the open server as "name (status)" (its home, the Apps
// page), its text channels, open DMs as queries, then the other servers as "(status)" windows. Unread windows are red,
// mentions bold, the open one lit. On desktop it runs across the top with the server menu and my settings link; on a
// phone it sits under the header and scrolls sideways. The window list (IrcSidebar) is the phone's drawer: the same
// windows as a tree (├─ └─) with counts. Chosen in web/src/sidebars.tsx.
// Part of the IRC layout: deleting components/irc/ and its entries removes it.
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { dmTitle, newSinceRead, type Channel } from "@bloom/shared";
import { isUnread, sidebarOrder } from "../../readState.ts";
import { rowMenuHandlers } from "../ChannelRowMenu.tsx";
import { shownDms } from "../DmList.tsx";
import { CloseButton } from "../icons.tsx";
import { NewChannelDialog } from "../NewChannelDialog.tsx";
import { ServerMenu } from "../ServerMenu.tsx";
import type { SidebarProps } from "../SidebarProps.ts";
import { branch, windowNote, windowState, type WindowState } from "./ircLogic.ts";
import "./irc.css";

interface Win {
  key: string;
  kind: "status" | "channel" | "query";
  label: string;
  state: WindowState;
  /** "12 new", "@ you", "here", "" */
  note: string;
  channel?: Channel;
  serverId: string;
  /** Another server's status window: switching to it. */
  other?: boolean;
}

/** Text channels in the sidebar's order (uncategorised first, then by category). */
const textOrder = (p: SidebarProps) => sidebarOrder(p.channels, p.categories, []);

function channelWindows(p: SidebarProps): Win[] {
  const unreadState = { read: p.read, me: p.me };
  const win = (c: Channel, label: string, kind: "channel" | "query"): Win => {
    const active = c.id === p.activeId && !p.inApps && !p.inBloom;
    const unread = isUnread(c, unreadState);
    const mentions = p.mentionCounts[c.id] ?? 0;
    const muted = p.muted.has(c.id);
    return {
      key: c.id,
      kind,
      label,
      serverId: p.serverId,
      channel: c,
      state: windowState({ active, unread, mentions, muted }),
      note: windowNote({ unreadCount: newSinceRead(c, p.read[c.id] ?? 0), mentions, muted, active }),
    };
  };
  const dms = shownDms(p.channels.filter((c) => c.kind === "dm"), p.byChannel, p.activeId, p.dmClosed);
  return [
    ...textOrder(p).map((c) => win(c, `#${c.name}`, "channel")),
    ...dms.map((c) => win(c, dmTitle(c, p.me, p.names), "query")),
  ];
}

/** The status window of the open server (its home, the Apps page) and of every other server. */
function serverWindow(p: SidebarProps, s: SidebarProps["servers"][number]): Win {
  const open = s.id === p.serverId;
  const u = open ? undefined : p.menu.unread[s.id];
  return {
    key: `status:${s.id}`,
    kind: "status",
    label: `${s.name} (status)`,
    serverId: s.id,
    other: !open,
    state: open ? (p.inApps ? "active" : "idle") : windowState({ active: false, unread: !!u?.unread, mentions: u?.mentions ?? 0, muted: false }),
    note: open ? (p.inApps ? "here" : "") : (u?.mentions ? "@ you" : u?.unread ? "new" : ""),
  };
}

function Tab({ w, p, children }: { w: Win; p: SidebarProps; children: ReactNode }) {
  const cls = `irc-tab ${w.state}`;
  if (w.kind === "status") {
    return w.other ? (
      <button type="button" className={cls} onClick={() => p.menu.onSwitch(w.serverId)}>{children}</button>
    ) : (
      <Link to={`/s/${w.serverId}/apps`} className={cls} aria-current={w.state === "active" ? "page" : undefined} onClick={p.onNavigate}>{children}</Link>
    );
  }
  return (
    <Link
      to={`/s/${w.serverId}/${w.channel!.id}`}
      className={cls}
      aria-current={w.state === "active" ? "page" : undefined}
      onClick={p.onNavigate}
      {...rowMenuHandlers(p.onConversationMenu, w.channel!.id)}
    >
      {children}
    </Link>
  );
}

/** The switchbar. `variant`: "desktop" has the server menu and my settings link; "phone" is only the windows. */
export function IrcStrip(p: SidebarProps & { variant: "desktop" | "phone" }) {
  const here = p.servers.find((s) => s.id === p.serverId);
  const wins: Win[] = [
    ...(here ? [serverWindow(p, here)] : []),
    ...channelWindows(p),
    ...p.servers.filter((s) => s.id !== p.serverId).map((s) => serverWindow(p, s)),
  ];
  const bar = useRef<HTMLDivElement>(null);
  const active = wins.find((w) => w.state === "active")?.key;
  // The open window stays in view when there are more windows than fit.
  useEffect(() => {
    bar.current?.querySelector<HTMLElement>(".irc-tab.active")?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active, p.variant]);
  return (
    <nav className={`irc-strip irc-strip-${p.variant}`} aria-label="Windows">
      {p.variant === "desktop" && (
        <div className="irc-strip-menu">
          <ServerMenu {...p.menu} collections={p.collections} openCollection={p.openCollection} appsOpen={p.inApps} onApps={p.onApps} onCollection={p.onCollection} onNewCollection={p.onNewCollection} />
        </div>
      )}
      <div className="irc-tabs" ref={bar}>
        {wins.map((w) => (
          <Tab key={w.key} w={w} p={p}>
            {w.label}
          </Tab>
        ))}
      </div>
      {p.variant === "desktop" && <div className="irc-strip-end">{p.footer}</div>}
    </nav>
  );
}

/** The phone's drawer: the window list as a tree. On desktop the sidebar is hidden (the switchbar is the list). */
export function IrcSidebar(p: SidebarProps) {
  const [making, setMaking] = useState<"channel" | "category" | null>(null);
  const channels = channelWindows(p);
  const text = channels.filter((w) => w.kind === "channel");
  const queries = channels.filter((w) => w.kind === "query");
  const row = (w: Win, i: number, n: number) => (
    <Tab key={w.key} w={w} p={p}>
      <span className="irc-branch">{` ${branch(i, n)}`}</span>
      <span className="irc-tree-name">{w.label}</span>
      <span className="irc-tree-note">{w.note}</span>
    </Tab>
  );
  return (
    <>
      <aside className="sidebar irc-sidebar" aria-label="Windows">
        <div className="sidebar-head">
          <ServerMenu
            {...p.menu}
            collections={p.collections}
            openCollection={p.openCollection}
            appsOpen={p.inApps}
            onApps={p.onApps}
            onCollection={p.onCollection}
            onNewCollection={p.onNewCollection}
            onNewChannel={p.isAdmin ? () => { p.onCloseDrawer(); setMaking("channel"); } : undefined}
            onNewCategory={p.isAdmin ? () => { p.onCloseDrawer(); setMaking("category"); } : undefined}
          />
          {p.canInvite && <button className="btn btn-small" onClick={p.onInvite}>Invite</button>}
          <CloseButton className="mobile-only" onClick={p.onCloseDrawer} aria-label="Close windows" />
        </div>
        <div className="sidebar-scroll">
          <div className="irc-tree" role="list">
            {p.servers.map((s) => {
              const open = s.id === p.serverId;
              const status = serverWindow(p, s);
              return (
                <div key={s.id} className="irc-tree-group" role="listitem">
                  <Tab w={status} p={p}>
                    <span className="irc-branch" />
                    <span className="irc-tree-name irc-tree-server">{s.name}</span>
                    <span className="irc-tree-note">{status.note}</span>
                  </Tab>
                  {open && text.map((w, i) => row(w, i, text.length))}
                </div>
              );
            })}
            <div className="irc-tree-group" role="listitem">
              <div className="irc-tab irc-tree-head"><span className="irc-branch" /><span className="irc-tree-name irc-tree-server">Queries</span></div>
              {queries.map((w, i) => row(w, i, queries.length))}
            </div>
          </div>
        </div>
        <div className="irc-tree-actions">
          <button type="button" className="irc-cmd irc-cmd-join" onClick={() => { p.onCloseDrawer(); p.menu.onJoin(); }}>/join server</button>
          <button type="button" className="irc-cmd irc-cmd-query" onClick={() => { p.onCloseDrawer(); p.onNewDm(); }}>/query nick</button>
        </div>
        {p.footer}
      </aside>
      {making && (
        <NewChannelDialog
          kind={making}
          categories={p.categories}
          onClose={() => setMaking(null)}
          onCreate={(name, categoryId) => (making === "category" ? p.createCategory(name) : p.createChannel(name, categoryId))}
        />
      )}
    </>
  );
}
